/**
 * Transcript Artifact Schema
 *
 * Contract for voice session transcripts stored as VAP artifacts.
 *
 * IMPORTANT:
 * - Scoring engine consumes `fullText` only (format unchanged across versions)
 * - `turns` must be preserved for audit, replay, and future features
 * - Schema version enables backward-compatible evolution
 *
 * VERSIONING:
 * - v1.0.0: Initial schema
 * - v1.1.0: Added optional phase field to turns (F-004)
 *           NOTE: fullText format unchanged to preserve scoring compatibility
 *
 * @module gemini/artifact-schema
 */

// SessionPhase type is used in TranscriptArtifactTurn.phase
import type { SessionPhase } from './provider';

// ============================================================================
// Transcript Artifact Schema
// ============================================================================

/**
 * Schema version for the transcript artifact format.
 * v1.1.0: Added phase field to turns (F-004)
 */
export const TRANSCRIPT_ARTIFACT_SCHEMA_VERSION = '1.1.0';

/**
 * A single turn in the conversation
 */
export interface TranscriptArtifactTurn {
  /** Speaker role */
  role: 'user' | 'agent';

  /** Transcribed text content */
  text: string;

  /** Turn timestamp (ISO 8601) */
  timestamp: string;

  /**
   * Session phase during which this turn occurred (F-004).
   * Optional for backwards compatibility with v1.0.0 artifacts.
   */
  phase?: SessionPhase;
}

/**
 * Transcript Artifact v1.x
 *
 * Complete transcript for storage and scoring.
 * v1.1.0: Added optional phase field to turns (F-004)
 */
export interface TranscriptArtifactV1 {
  /** Schema version for format evolution */
  schemaVersion: '1.0.0' | '1.1.0';

  /** Voice session provider used (for audit and debugging) */
  provider: 'gemini' | 'pipecat' | 'livekit';

  /** Session start time (ISO 8601) */
  sessionStartedAt: string;

  /** Session end time (ISO 8601) */
  sessionEndedAt: string;

  /**
   * Structured turns (preserved for audit and future features).
   * In v1.1.0+, turns may include a `phase` field for warm-up vs assessment context.
   */
  turns: TranscriptArtifactTurn[];

  /**
   * Full text for scoring engine consumption.
   *
   * Format (unchanged across versions):
   * [AGENT] text
   * [LEARNER] text
   * [AGENT] text
   * ...
   *
   * Scoring engine reads ONLY this field.
   * Phase information is stored in `turns` only, not in fullText,
   * to maintain backwards compatibility with existing scoring.
   */
  fullText: string;
}

// ============================================================================
// Factory Functions
// ============================================================================

/**
 * Create a transcript artifact from session turns
 *
 * @param turns - Array of transcript turns from voice session
 * @param provider - Provider that generated the transcript
 * @param sessionStartedAt - Session start time
 * @param sessionEndedAt - Session end time
 */
export function createTranscriptArtifact(
  turns: TranscriptArtifactTurn[],
  provider: TranscriptArtifactV1['provider'],
  sessionStartedAt: string,
  sessionEndedAt: string
): TranscriptArtifactV1 {
  // Check if any turns have phase information (determines schema version)
  const hasPhaseInfo = turns.some((turn) => turn.phase !== undefined);

  // Generate fullText in scoring-compatible format
  // NOTE: Format is unchanged across versions to preserve scoring compatibility
  // Phase information is stored in turns only, not in fullText
  const fullText = turns
    .map((turn) => {
      const speaker = turn.role === 'user' ? 'LEARNER' : 'AGENT';
      return `[${speaker}] ${turn.text}`;
    })
    .join('\n\n');

  return {
    // Use v1.1.0 if turns have phase data, v1.0.0 otherwise
    schemaVersion: hasPhaseInfo ? '1.1.0' : '1.0.0',
    provider,
    sessionStartedAt,
    sessionEndedAt,
    turns,
    fullText,
  };
}

/**
 * Extract fullText from a transcript artifact (for scoring)
 *
 * Scoring engine should call this function.
 */
export function extractFullTextForScoring(artifact: TranscriptArtifactV1): string {
  return artifact.fullText;
}

/**
 * Validate a transcript artifact against v1.x schema
 */
export function validateTranscriptArtifact(
  data: unknown
): data is TranscriptArtifactV1 {
  if (typeof data !== 'object' || data === null) {
    return false;
  }

  const obj = data as Record<string, unknown>;

  // Accept both v1.0.0 and v1.1.0
  const validVersion =
    obj.schemaVersion === '1.0.0' || obj.schemaVersion === '1.1.0';

  return (
    validVersion &&
    typeof obj.provider === 'string' &&
    ['gemini', 'pipecat', 'livekit'].includes(obj.provider) &&
    typeof obj.sessionStartedAt === 'string' &&
    typeof obj.sessionEndedAt === 'string' &&
    Array.isArray(obj.turns) &&
    typeof obj.fullText === 'string'
  );
}
